/**
 * ChatPage — the agentic chat experience.
 *
 * Layout (asymmetric grid):
 *   ┌────────────────────────────────────────────┬──────────────────┐
 *   │  Conversation transcript                   │  Sessions        │
 *   │  (max-w-[70ch] for editorial measure)      │  + Memory        │
 *   ├────────────────────────────────────────────┤                  │
 *   │  Composer  ──────────────  Send →          │                  │
 *   └────────────────────────────────────────────┴──────────────────┘
 *
 * SSE frame types handled:
 *   init    → backend echoes the resolved session_id
 *   thought → agent reasoning step (shown in AgentProgress timeline)
 *   chunk   → one LLM token (or token group) — appended to streamBuffer
 *   done    → final terminator
 *   error   → backend / model failure (rendered as a toast)
 *
 * Additional features:
 *   - Full-screen table viewer (TableFullscreenModal) — click ↗ on any table
 *   - Save table to "My Tables" (savedTablesStore) — from full-screen or inline ⊕
 *   - Structured agent progress (AgentProgress) — live step-by-step timeline
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import {
  Plus,
  Send,
  Trash2,
  Brain,
  CornerDownLeft,
  CircleDot,
} from "lucide-react";

import * as chatService from "../../services/chatService";
import { toast } from "../../stores/toastStore";
import { confirm } from "../../components/ConfirmDialog";
import { useSavedTablesStore } from "../../stores/savedTablesStore";
import MemoryDrawer from "./MemoryDrawer";
import AgentProgress from "./AgentProgress";
import TableFullscreenModal, { parseHtmlTable } from "../../components/TableFullscreenModal";

// Marked configuration — match the editorial aesthetic.
marked.setOptions({ breaks: true, gfm: true });

export default function ChatPage() {
  const [sessions,        setSessions]        = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages,        setMessages]        = useState([]);   // [{ role, content }]
  const [input,           setInput]           = useState("");
  const [streaming,       setStreaming]       = useState(false);
  const [streamBuffer,    setStreamBuffer]    = useState("");
  const [agentSteps,      setAgentSteps]      = useState([]);   // thought frames
  const [memoryOpen,      setMemoryOpen]      = useState(false);

  // Full-screen table state
  const [fullscreenTable, setFullscreenTable] = useState(null); // { tableHtml, columns, rows, sourceQuery }

  // The query that produced the currently-streaming answer (used for table saving)
  const currentQueryRef = useRef("");

  const transcriptRef = useRef(null);
  const composerRef   = useRef(null);

  const saveTable = useSavedTablesStore((s) => s.saveTable);

  // ─── Initial session list ──────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    chatService.listSessions().then(
      (list) => alive && setSessions(list || []),
      (err)  => toast.error(err.message || "Couldn't load sessions"),
    );
    return () => { alive = false; };
  }, []);

  // ─── Auto-scroll transcript ────────────────────────────────────────────
  useEffect(() => {
    if (!transcriptRef.current) return;
    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [messages, streamBuffer, agentSteps]);

  // ─── Auto-resize composer ──────────────────────────────────────────────
  function autoResize() {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 240) + "px";
  }

  // ─── Load a saved session ──────────────────────────────────────────────
  async function loadSession(sessionId) {
    if (streaming) {
      toast.info("Wait for the current answer to finish");
      return;
    }
    setActiveSessionId(sessionId);
    setMessages([]);
    setStreamBuffer("");
    setAgentSteps([]);
    try {
      const history = await chatService.fetchHistory(sessionId);
      const flat = history.flatMap((h) => [
        { role: "user",      content: h.user },
        { role: "assistant", content: h.bot  },
      ]);
      setMessages(flat);
    } catch (err) {
      toast.error(err.message || "Couldn't load that session");
    }
  }

  function newChat() {
    if (streaming) {
      toast.info("Wait for the current answer to finish");
      return;
    }
    setActiveSessionId(null);
    setMessages([]);
    setStreamBuffer("");
    setAgentSteps([]);
    composerRef.current?.focus();
  }

  // ─── Delete a session ──────────────────────────────────────────────────
  async function onDeleteSession(sessionId, e) {
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete this session?",
      description: "The conversation history will be permanently removed.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await chatService.deleteSession(sessionId);
      setSessions((curr) => curr.filter((s) => s.session_id !== sessionId));
      if (activeSessionId === sessionId) newChat();
      toast.success("Session deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  }

  // ─── Send a query ──────────────────────────────────────────────────────
  async function sendQuery(e) {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || streaming) return;

    currentQueryRef.current = q;
    setInput("");
    autoResize();
    setMessages((m) => [...m, { role: "user", content: q }]);
    setStreaming(true);
    setStreamBuffer("");
    setAgentSteps([]);

    let resolvedSessionId = activeSessionId;
    let answer = "";

    try {
      for await (const frame of chatService.streamChat({ query: q, session_id: activeSessionId })) {
        if (frame.type === "init") {
          if (!resolvedSessionId) {
            resolvedSessionId = frame.session_id;
            setActiveSessionId(resolvedSessionId);
            setSessions((curr) => [
              {
                session_id: resolvedSessionId,
                title: q.length > 40 ? q.slice(0, 40) + "…" : q,
                created_at: new Date().toISOString(),
              },
              ...curr,
            ]);
          }

        } else if (frame.type === "thought") {
          // Mark the previous active step as done, add the new step as active
          setAgentSteps((prev) => [
            ...prev.map((s) => ({ ...s, status: "done" })),
            {
              step:   frame.step   || "tool",
              label:  frame.label  || "Processing…",
              detail: frame.detail || "",
              status: "active",
            },
          ]);

        } else if (frame.type === "chunk") {
          answer += frame.content;
          setStreamBuffer(answer);

        } else if (frame.type === "error") {
          toast.error(frame.error || "Streaming error");
          break;
        }
      }
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      // Mark all agent steps as done
      setAgentSteps((prev) => prev.map((s) => ({ ...s, status: "done" })));

      if (answer) {
        setMessages((m) => [...m, { role: "assistant", content: answer }]);
      }
      setStreaming(false);
      setStreamBuffer("");

      setTimeout(() => {
        chatService.listSessions().then(
          (list) => setSessions(list || []),
          () => { /* swallowed */ },
        );
      }, 500);
    }
  }

  function handlePromptSelect(text) {
    setInput(text);
    setTimeout(() => {
      composerRef.current?.focus();
      autoResize();
    }, 0);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuery();
    }
  }

  // ─── Full-screen table handling ────────────────────────────────────────
  // We use event delegation on the transcript div rather than wiring per-Turn
  // because Turn renders dangerouslySetInnerHTML and we can't attach React
  // handlers inside it easily.
  function handleTranscriptClick(e) {
    const btn = e.target.closest("[data-table-expand]");
    if (!btn) return;

    const tableScroll = btn.closest(".table-scroll");
    if (!tableScroll) return;

    const tableEl = tableScroll.querySelector("table");
    if (!tableEl) return;

    const tableHtml = tableEl.outerHTML;
    const { columns, rows } = parseHtmlTable(tableHtml);

    setFullscreenTable({
      tableHtml,
      columns,
      rows,
      sourceQuery: currentQueryRef.current || "",
    });
  }

  // Also capture "save inline" button clicks
  function handleSaveInlineClick(e) {
    const btn = e.target.closest("[data-table-save]");
    if (!btn) return;

    const tableScroll = btn.closest(".table-scroll");
    if (!tableScroll) return;

    const tableEl = tableScroll.querySelector("table");
    if (!tableEl) return;

    const { columns, rows } = parseHtmlTable(tableEl.outerHTML);
    const name = (currentQueryRef.current || "Saved table").slice(0, 60);
    saveTable({ name, columns, rows, sourceQuery: currentQueryRef.current });
    toast.success("Table saved to My Tables");
  }

  const onTranscriptClick = useCallback((e) => {
    handleTranscriptClick(e);
    handleSaveInlineClick(e);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFullscreenSave({ name, columns, rows, sourceQuery }) {
    saveTable({ name, columns, rows, sourceQuery });
    toast.success("Table saved to My Tables");
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] h-full min-h-0">
      {/* ─── Conversation column ─────────────────────────────────────── */}
      <section className="flex flex-col min-h-0">
        {/* Page header */}
        <div className="px-8 lg:px-12 pt-8 pb-4 border-b border-[var(--color-rule)] flex items-baseline justify-between gap-4">
          <div className="reveal">
            <div className="eyebrow">CONVERSATION</div>
            <h1 className="display text-[var(--text-2xl)] mt-2">Ask the data.</h1>
          </div>
          <div className="hidden md:flex items-center gap-2 mono text-[var(--text-xs)] text-[var(--color-ink-3)]">
            {streaming ? (
              <>
                <CircleDot className="w-3 h-3 animate-pulse text-[var(--color-accent)]" />
                streaming
              </>
            ) : (
              <span>idle</span>
            )}
          </div>
        </div>

        {/* Transcript */}
        <div
          ref={transcriptRef}
          className="flex-1 overflow-y-auto px-8 lg:px-12 py-10"
          onClick={onTranscriptClick}
        >
          {messages.length === 0 && !streaming && <EmptyState onSelectPrompt={handlePromptSelect} />}

          <div className="max-w-[70ch] mx-auto space-y-10">
            {messages.map((m, i) => (
              <Turn key={i} role={m.role} content={m.content} />
            ))}

            {/* Streaming assistant turn */}
            {streaming && (
              <div>
                {/* Agent progress timeline */}
                {agentSteps.length > 0 && (
                  <AgentProgress steps={agentSteps} streaming={streaming} />
                )}

                {/* Streaming text or waiting dots */}
                {streamBuffer ? (
                  <Turn role="assistant" content={streamBuffer} streaming />
                ) : (
                  <div className="flex items-center gap-3 text-[var(--color-ink-3)] text-[var(--text-sm)] mt-4">
                    <span className="eyebrow !text-[var(--color-ink-3)]">ASSISTANT</span>
                    <span className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[var(--color-ink-3)] rounded-full animate-pulse" style={{ animationDelay: "0ms"   }} />
                      <span className="w-1.5 h-1.5 bg-[var(--color-ink-3)] rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-[var(--color-ink-3)] rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Completed agent progress (stays visible until collapsed) */}
            {!streaming && agentSteps.length > 0 && (
              <AgentProgress steps={agentSteps} streaming={false} />
            )}
          </div>
        </div>

        {/* Composer */}
        <form
          onSubmit={sendQuery}
          className="border-t border-[var(--color-rule)] px-8 lg:px-12 py-5 bg-[var(--color-paper)]"
        >
          <div className="max-w-[70ch] mx-auto">
            <div className="flex items-end gap-3">
              <textarea
                ref={composerRef}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoResize(); }}
                onKeyDown={handleKey}
                rows={1}
                placeholder={
                  streaming
                    ? "Streaming an answer…"
                    : "Ask a question — e.g. 'which instructors are overloaded this AY?'"
                }
                disabled={streaming}
                className="
                  flex-1 resize-none
                  bg-transparent
                  border-0 border-b border-[var(--color-rule-strong)] focus:border-[var(--color-accent)]
                  py-3
                  text-[var(--text-base)] text-[var(--color-ink)]
                  placeholder:text-[var(--color-ink-3)]
                  outline-none transition-colors
                "
              />
              <button
                type="submit"
                disabled={!input.trim() || streaming}
                className="btn btn-primary"
                aria-label="Send"
              >
                <Send className="w-3.5 h-3.5" />
                Send
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)]">
              <span className="flex items-center gap-1.5">
                <CornerDownLeft className="w-3 h-3" /> enter · send
                <span className="ml-3">shift + enter · newline</span>
              </span>
              <span>grounded in active department</span>
            </div>
          </div>
        </form>
      </section>

      {/* ─── Right rail · sessions + memory ───────────────────────────── */}
      <aside className="hidden lg:flex flex-col border-l border-[var(--color-rule)] min-h-0">
        <div className="px-6 pt-8 pb-4 border-b border-[var(--color-rule)] flex items-center justify-between">
          <div className="eyebrow">SESSIONS</div>
          <button
            type="button"
            onClick={newChat}
            className="flex items-center gap-1.5 mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-2)] hover:text-[var(--color-accent)] transition-colors"
            aria-label="New chat"
          >
            <Plus className="w-3 h-3" /> NEW
          </button>
        </div>

        <ul className="flex-1 overflow-y-auto py-2">
          {sessions.length === 0 && (
            <li className="px-6 py-4 text-[var(--text-sm)] text-[var(--color-ink-3)]">
              No sessions yet. Ask a question below.
            </li>
          )}
          {sessions.map((s) => {
            const isActive = s.session_id === activeSessionId;
            return (
              <li
                key={s.session_id}
                className={[
                  "group px-6 py-3 cursor-pointer flex items-start gap-2",
                  isActive
                    ? "border-l-2 border-[var(--color-accent)] -ml-px bg-[var(--color-paper-2)]/40"
                    : "border-l-2 border-transparent -ml-px hover:bg-[var(--color-paper-2)]/30",
                ].join(" ")}
                onClick={() => loadSession(s.session_id)}
              >
                <span className="flex-1 min-w-0">
                  <span className={[
                    "block text-[var(--text-sm)] truncate",
                    isActive ? "text-[var(--color-ink)]" : "text-[var(--color-ink-2)]",
                  ].join(" ")}>
                    {s.title || "Untitled"}
                  </span>
                  <span className="mono text-[10px] text-[var(--color-ink-3)] tracking-wide">
                    {formatDate(s.created_at)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={(e) => onDeleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-[var(--color-ink-3)] hover:text-[var(--color-accent)]"
                  aria-label="Delete session"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>

        {/* Memory CTA */}
        <div className="border-t border-[var(--color-rule)] p-6">
          <button
            type="button"
            onClick={() => setMemoryOpen(true)}
            className="w-full flex items-center gap-2 text-left text-[var(--color-ink-2)] hover:text-[var(--color-ink)] transition-colors"
          >
            <Brain className="w-4 h-4" />
            <span className="text-[var(--text-sm)] flex-1">Global memory</span>
            <span className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)]">edit</span>
          </button>
        </div>
      </aside>

      <MemoryDrawer open={memoryOpen} onOpenChange={setMemoryOpen} />

      {/* Full-screen table modal */}
      <TableFullscreenModal
        open={fullscreenTable !== null}
        tableHtml={fullscreenTable?.tableHtml}
        columns={fullscreenTable?.columns ?? []}
        rows={fullscreenTable?.rows ?? []}
        sourceQuery={fullscreenTable?.sourceQuery ?? ""}
        onClose={() => setFullscreenTable(null)}
        onSave={handleFullscreenSave}
      />
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function EmptyState({ onSelectPrompt }) {
  const prompts = [
    "Which instructors are overloaded this academic year?",
    "Which instructors are underloaded relative to their target CCH?",
    "Show the CCH share by section status for SADT-CS.",
    "Which courses depend on a single instructor?",
    "How is the load distributed across Fall, Winter, and Spring?",
    "How many faculty of each contract type are active?",
    "What proportion of total CCH is associated with non-teaching activities?",
    "Which courses are driving the highest instructional load across all semesters?",
    "Are there course sections missing instructor assignments?",
    "What is the average teaching load per active instructor by contract type?",
    "Which instructors have non-teaching assignments this semester?",
    "If all pending sections were activated, how would total CCH change by semester?",
  ];

  return (
    <div className="max-w-[60ch] mx-auto pt-12 lg:pt-20">
      <div className="reveal">
        <div className="eyebrow">PROMPTS · TO START</div>
      </div>
      <h2 className="reveal display text-[var(--text-display)] leading-[0.95] mt-6"
          style={{ animationDelay: "120ms" }}>
        What do the
        <br />
        <span className="italic">numbers</span> say?
      </h2>
      <p className="reveal text-[var(--text-base)] text-[var(--color-ink-2)] mt-6 leading-[1.6] max-w-[44ch]"
         style={{ animationDelay: "200ms" }}>
        The agent reads from your department's calculated tables —
        <span className="mono text-[var(--text-sm)]"> sadt_calculated_load</span>,
        <span className="mono text-[var(--text-sm)]"> sadt_overload_summary</span>, and the rest.
        Ask anything you'd ask the spreadsheet.
      </p>

      <ul className="reveal mt-10 space-y-3" style={{ animationDelay: "280ms" }}>
        {prompts.map((p, i) => (
          <li key={i}>
            <PromptSuggestion text={p} onSelect={onSelectPrompt} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PromptSuggestion({ text, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(text)}
      className="
        text-left text-[var(--text-base)]
        text-[var(--color-ink-2)] hover:text-[var(--color-ink)]
        transition-colors
        link
      "
    >
      → {text}
    </button>
  );
}

function Turn({ role, content, streaming = false }) {
  const isUser = role === "user";

  const processedHtml = useMemo(() => {
    if (isUser) return null;
    const raw = marked.parse(content || "");
    // Inject scroll wrapper + action buttons around every <table>...</table> block
    return raw.replace(
      /<table([\s\S]*?)<\/table>/g,
      (_, inner) => `
        <div class="table-scroll">
          <div class="table-actions">
            <button data-table-save title="Save to My Tables" class="table-action-btn" aria-label="Save table">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save
            </button>
            <button data-table-expand title="Open full-screen" class="table-action-btn table-action-expand" aria-label="Expand table">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              Expand
            </button>
          </div>
          <table${inner}</table>
        </div>
      `,
    );
  }, [isUser, content]);

  return (
    <div className={isUser ? "flex justify-end" : ""}>
      <div className={isUser ? "max-w-[44ch]" : "w-full"}>
        <div className="eyebrow mb-3">
          {isUser ? "YOU" : "ASSISTANT"}
          {streaming && <span className="ml-2 mono text-[10px] text-[var(--color-accent)]">streaming</span>}
        </div>
        {isUser ? (
          <p className="text-[var(--text-base)] text-[var(--color-ink)] whitespace-pre-wrap leading-[1.6]">
            {content}
          </p>
        ) : (
          <div
            className="prose-editorial text-[var(--text-base)] text-[var(--color-ink)] leading-[1.65]"
            dangerouslySetInnerHTML={{ __html: processedHtml || "" }}
          />
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
